import React, { useState, useEffect } from "react";
import {
  ChevronRight,
  Play,
  Pause,
  Plus,
  Search,
  ZoomIn,
  ZoomOut,
  Grid3x3,
  Save,
} from "lucide-react";

interface Component {
  id: string;
  type: "source" | "decision" | "target";
  icon: string;
  title: string;
  subtitle: string;
}

interface WorkflowElement {
  id: string;
  type: "source" | "decision" | "target";
  title: string;
  position: { top: number; left: number };
  metrics: { label: string; value: string | number }[];
}

interface TestEvent {
  time: string;
  source: string;
  message: string;
  status: "routed" | "blocked";
  statusText: string;
  response?: string;
}

const ClinicalWorkflowCanvas: React.FC = () => {
  const [testMode, setTestMode] = useState(false);
  const [testPanelOpen, setTestPanelOpen] = useState(false);
  const [metrics, setMetrics] = useState<Record<string, number>>({
    icuEvents: 247,
    icuActive: 12,
    filterRate: 82,
    filterRules: 7,
    rnDelivered: 43,
    labEvents: 89,
    labCritical: 3,
    mdSent: 18,
  });

  const components: Component[] = [
    // Source Systems
    {
      id: "1",
      type: "source",
      icon: "🏥",
      title: "Nurse Call System",
      subtitle: "Hill-Rom NaviCare",
    },
    {
      id: "2",
      type: "source",
      icon: "📊",
      title: "Patient Monitor",
      subtitle: "Philips IntelliVue",
    },
    {
      id: "3",
      type: "source",
      icon: "🔬",
      title: "Lab System",
      subtitle: "Epic Beaker",
    },
    // Decision Blocks
    {
      id: "4",
      type: "decision",
      icon: "⚖️",
      title: "Priority Filter",
      subtitle: "Route by severity",
    },
    {
      id: "5",
      type: "decision",
      icon: "⏰",
      title: "Time-Based Rule",
      subtitle: "Shift scheduling",
    },
    {
      id: "6",
      type: "decision",
      icon: "🔄",
      title: "Escalation Logic",
      subtitle: "Multi-tier routing",
    },
    // Notification Targets
    {
      id: "7",
      type: "target",
      icon: "📱",
      title: "Mobile App",
      subtitle: "Vocera Platform",
    },
    {
      id: "8",
      type: "target",
      icon: "📟",
      title: "Pager System",
      subtitle: "Spok Console",
    },
    {
      id: "9",
      type: "target",
      icon: "💬",
      title: "Team Chat",
      subtitle: "Microsoft Teams",
    },
  ];

  const workflowElements: WorkflowElement[] = [
    {
      id: "w1",
      type: "source",
      title: "ICU Monitors",
      position: { top: 100, left: 100 },
      metrics: [
        { label: "Events/hr", value: metrics.icuEvents },
        { label: "Active", value: metrics.icuActive },
      ],
    },
    {
      id: "w2",
      type: "decision",
      title: "Critical Alert Filter",
      position: { top: 120, left: 400 },
      metrics: [
        { label: "Filtered", value: `${metrics.filterRate}%` },
        { label: "Rules", value: metrics.filterRules },
      ],
    },
    {
      id: "w3",
      type: "target",
      title: "RN Mobile Devices",
      position: { top: 80, left: 700 },
      metrics: [
        { label: "Delivered", value: metrics.rnDelivered },
        { label: "Avg Response", value: "1.2m" },
      ],
    },
    {
      id: "w4",
      type: "source",
      title: "Lab Results",
      position: { top: 280, left: 100 },
      metrics: [
        { label: "Events/hr", value: metrics.labEvents },
        { label: "Critical", value: metrics.labCritical },
      ],
    },
    {
      id: "w5",
      type: "target",
      title: "Physician Pagers",
      position: { top: 240, left: 700 },
      metrics: [
        { label: "Sent", value: metrics.mdSent },
        { label: "Acknowledged", value: "94%" },
      ],
    },
  ];

  const testEvents: TestEvent[] = [
    {
      time: "14:32:11",
      source: "ICU-04",
      message: "High heart rate alert (152 bpm) - Patient Johnson, Rm 412",
      status: "routed",
      statusText: "Routed to RN",
      response: "Response: 0.8m",
    },
    {
      time: "14:31:45",
      source: "LAB",
      message: "Critical potassium level (6.8 mEq/L) - Patient Davis, Rm 308",
      status: "routed",
      statusText: "Escalated to MD",
      response: "Response: 1.2m",
    },
    {
      time: "14:31:22",
      source: "Nurse Call",
      message: "Call button activated - Patient Miller, Rm 215",
      status: "blocked",
      statusText: "Filtered",
      response: "Low priority",
    },
    {
      time: "14:30:58",
      source: "ICU-02",
      message: "SpO2 dropping (88%) - Patient Thompson, Rm 401",
      status: "routed",
      statusText: "Routed to RT",
      response: "Response: 0.4m",
    },
    {
      time: "14:30:15",
      source: "LAB",
      message: "Troponin elevated (0.42 ng/mL) - Patient Wilson, Rm 506",
      status: "routed",
      statusText: "Escalated to Cardiology",
      response: "Response: 2.1m",
    },
  ];

  useEffect(() => {
    if (testMode) {
      const interval = setInterval(() => {
        setMetrics((prev) => ({
          icuEvents: Math.max(
            0,
            prev.icuEvents + Math.floor(Math.random() * 5) - 2
          ),
          icuActive: Math.max(
            0,
            prev.icuActive + Math.floor(Math.random() * 3) - 1
          ),
          filterRate: Math.max(
            0,
            Math.min(100, prev.filterRate + Math.floor(Math.random() * 5) - 2)
          ),
          filterRules: prev.filterRules,
          rnDelivered: Math.max(
            0,
            prev.rnDelivered + Math.floor(Math.random() * 3) - 1
          ),
          labEvents: Math.max(
            0,
            prev.labEvents + Math.floor(Math.random() * 5) - 2
          ),
          labCritical: Math.max(
            0,
            prev.labCritical + Math.floor(Math.random() * 2)
          ),
          mdSent: Math.max(0, prev.mdSent + Math.floor(Math.random() * 3) - 1),
        }));
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [testMode]);

  const toggleTestMode = () => {
    setTestMode(!testMode);
    setTestPanelOpen(!testPanelOpen);
  };

  const getComponentColorClasses = (type: string) => {
    switch (type) {
      case "source":
        return {
          bg: "bg-blue-50",
          text: "text-blue-700",
          border: "border-blue-600",
        };
      case "decision":
        return {
          bg: "bg-orange-50",
          text: "text-orange-700",
          border: "border-orange-600",
        };
      case "target":
        return {
          bg: "bg-green-50",
          text: "text-green-700",
          border: "border-green-600",
        };
      default:
        return {
          bg: "bg-gray-50",
          text: "text-gray-700",
          border: "border-gray-600",
        };
    }
  };

  return (
    <div className="h-screen flex flex-col bg-gray-100 overflow-hidden">
      {/* Header */}
      <header className="bg-slate-800 text-white px-6 py-3 flex items-center justify-between shadow-md">
        <div className="flex items-center gap-3">
          <span className="text-2xl">⚡</span>
          <h1 className="text-xl font-semibold">Connexall Workflow Designer</h1>
        </div>
        <div className="flex items-center gap-4">
          <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
            v2.4.1 - Autosaved
          </span>
          <button
            onClick={toggleTestMode}
            className={`px-5 py-2 rounded-md font-medium transition-all duration-300 flex items-center gap-2 ${
              testMode
                ? "bg-green-600 hover:bg-green-700 animate-pulse shadow-lg"
                : "bg-red-600 hover:bg-red-700 hover:-translate-y-0.5 hover:shadow-lg"
            }`}
          >
            {testMode ? <Pause size={16} /> : <Play size={16} />}
            {testMode ? "Stop Test" : "Live Test Mode"}
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className="w-72 bg-white border-r border-gray-200 p-5 overflow-y-auto">
          {/* Source Systems */}
          <div className="mb-7">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">
              Source Systems
            </h3>
            {components.slice(0, 3).map((component) => {
              const colors = getComponentColorClasses(component.type);
              return (
                <div
                  key={component.id}
                  className="bg-gray-50 border-2 border-gray-200 rounded-lg p-3 mb-3 cursor-grab hover:translate-x-1 hover:border-blue-500 hover:shadow-md transition-all duration-200 flex items-center gap-3"
                >
                  <div
                    className={`w-10 h-10 ${colors.bg} rounded-lg flex items-center justify-center text-xl`}
                  >
                    {component.icon}
                  </div>
                  <div>
                    <h4 className="text-sm font-medium">{component.title}</h4>
                    <span className="text-xs text-gray-500">
                      {component.subtitle}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Decision Blocks */}
          <div className="mb-7">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">
              Decision Blocks
            </h3>
            {components.slice(3, 6).map((component) => {
              const colors = getComponentColorClasses(component.type);
              return (
                <div
                  key={component.id}
                  className="bg-gray-50 border-2 border-gray-200 rounded-lg p-3 mb-3 cursor-grab hover:translate-x-1 hover:border-blue-500 hover:shadow-md transition-all duration-200 flex items-center gap-3"
                >
                  <div
                    className={`w-10 h-10 ${colors.bg} rounded-lg flex items-center justify-center text-xl`}
                  >
                    {component.icon}
                  </div>
                  <div>
                    <h4 className="text-sm font-medium">{component.title}</h4>
                    <span className="text-xs text-gray-500">
                      {component.subtitle}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Notification Targets */}
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">
              Notification Targets
            </h3>
            {components.slice(6).map((component) => {
              const colors = getComponentColorClasses(component.type);
              return (
                <div
                  key={component.id}
                  className="bg-gray-50 border-2 border-gray-200 rounded-lg p-3 mb-3 cursor-grab hover:translate-x-1 hover:border-blue-500 hover:shadow-md transition-all duration-200 flex items-center gap-3"
                >
                  <div
                    className={`w-10 h-10 ${colors.bg} rounded-lg flex items-center justify-center text-xl`}
                  >
                    {component.icon}
                  </div>
                  <div>
                    <h4 className="text-sm font-medium">{component.title}</h4>
                    <span className="text-xs text-gray-500">
                      {component.subtitle}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </aside>

        {/* Canvas */}
        <main
          className="flex-1 relative bg-gray-50"
          style={{
            backgroundImage:
              "radial-gradient(circle, #e0e0e0 1px, transparent 1px)",
            backgroundSize: "20px 20px",
          }}
        >
          {/* Toolbar */}
          <div className="absolute top-5 right-5 flex gap-2 bg-white p-2 rounded-lg shadow-md">
            <button className="w-9 h-9 bg-gray-50 rounded-md hover:bg-gray-100 hover:-translate-y-0.5 transition-all flex items-center justify-center">
              <ZoomIn size={18} />
            </button>
            <button className="w-9 h-9 bg-gray-50 rounded-md hover:bg-gray-100 hover:-translate-y-0.5 transition-all flex items-center justify-center">
              <ZoomOut size={18} />
            </button>
            <button className="w-9 h-9 bg-gray-50 rounded-md hover:bg-gray-100 hover:-translate-y-0.5 transition-all flex items-center justify-center">
              <Grid3x3 size={18} />
            </button>
            <button className="w-9 h-9 bg-gray-50 rounded-md hover:bg-gray-100 hover:-translate-y-0.5 transition-all flex items-center justify-center">
              <Save size={18} />
            </button>
          </div>

          {/* Workflow Elements */}
          {workflowElements.map((element) => {
            const colors = getComponentColorClasses(element.type);
            return (
              <div
                key={element.id}
                className={`absolute bg-white rounded-xl shadow-lg p-4 min-w-[180px] cursor-move hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 border-l-4 ${colors.border}`}
                style={{
                  top: element.position.top,
                  left: element.position.left,
                }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  <h4 className="font-semibold text-sm">{element.title}</h4>
                </div>
                <div className="flex gap-4 text-xs text-gray-600">
                  {element.metrics.map((metric, idx) => (
                    <div key={idx} className="flex flex-col gap-0.5">
                      <span>{metric.label}</span>
                      <span className="text-lg font-semibold text-gray-800">
                        {metric.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Connection Lines */}
          <svg
            className="absolute inset-0 pointer-events-none"
            style={{ width: "100%", height: "100%" }}
          >
            <defs>
              <marker
                id="arrowhead"
                markerWidth="10"
                markerHeight="7"
                refX="10"
                refY="3.5"
                orient="auto"
              >
                <polygon points="0 0, 10 3.5, 0 7" fill="#007bff" />
              </marker>
            </defs>
            <line
              x1="280"
              y1="140"
              x2="400"
              y2="140"
              stroke="#007bff"
              strokeWidth="2"
              markerEnd="url(#arrowhead)"
              opacity="0.6"
            />
            <line
              x1="580"
              y1="120"
              x2="700"
              y2="100"
              stroke="#007bff"
              strokeWidth="2"
              markerEnd="url(#arrowhead)"
              opacity="0.6"
            />
            <line
              x1="580"
              y1="160"
              x2="700"
              y2="260"
              stroke="#007bff"
              strokeWidth="2"
              markerEnd="url(#arrowhead)"
              opacity="0.6"
            />
            <line
              x1="280"
              y1="300"
              x2="700"
              y2="300"
              stroke="#007bff"
              strokeWidth="2"
              markerEnd="url(#arrowhead)"
              opacity="0.6"
            />
          </svg>

          {/* FAB */}
          <button className="absolute bottom-6 right-6 w-14 h-14 bg-blue-600 text-white rounded-full shadow-lg hover:scale-110 hover:rotate-90 hover:shadow-xl transition-all duration-300 flex items-center justify-center">
            <Plus size={24} />
          </button>
        </main>
      </div>

      {/* Test Panel */}
      <div
        className={`absolute bottom-0 left-0 right-0 h-80 bg-white border-t-2 border-gray-200 shadow-2xl transition-all duration-300 ${
          testPanelOpen ? "translate-y-0" : "translate-y-full"
        }`}
      >
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
            <h3 className="font-semibold">
              Live Test Results - Replaying Yesterday's Events
            </h3>
          </div>
          <span className="text-sm text-gray-500">1,247 events processed</span>
        </div>
        <div className="px-6 py-5 overflow-y-auto h-64">
          {testEvents.map((event, idx) => (
            <div
              key={idx}
              className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg mb-3 animate-slideIn"
            >
              <span className="text-xs text-gray-500 min-w-[60px]">
                {event.time}
              </span>
              <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded text-xs font-medium">
                {event.source}
              </span>
              <span className="flex-1 text-sm">{event.message}</span>
              <div className="flex items-center gap-2 text-xs">
                <span
                  className={`px-2 py-1 rounded font-medium ${
                    event.status === "routed"
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {event.statusText}
                </span>
                <span className="text-gray-500">{event.response}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ClinicalWorkflowCanvas;
